import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AdminBundle,
  AdminCategory,
  ApiError,
  createBundle,
  deleteBundle,
  fetchAdminBundles,
  fetchAdminCatalog,
  fetchTags,
  Tag,
  updateBundle,
} from '../../../api/client';
import { toCents } from '../../../shared/functions/formatter';
import { BundleFormState, toInput } from './types';

/** Surface a failure, or hand an expired session back to App to log out. */
function report(error: unknown, onUnauthorized: () => void, setError: (message: string) => void, fallback: string) {
  if (error instanceof ApiError && error.status === 401) {
    onUnauthorized();
    return;
  }
  setError(error instanceof Error ? error.message : fallback);
}

/**
 * The bundles page's data and mutations.
 *
 * Owns the catalogue the editor needs, the saved bundles, the request feedback
 * and which row is busy. The editor owns its own draft; this hook only sees a
 * finished form when it is submitted.
 */
export function useBundles(onUnauthorized: () => void) {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [bundles, setBundles] = useState<AdminBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchAdminCatalog(), fetchAdminBundles(), fetchTags()])
      .then(([nextCategories, nextBundles, nextTags]) => {
        if (cancelled) return;
        setCategories(nextCategories);
        setBundles(nextBundles);
        setTags(nextTags);
        setError('');
      })
      .catch((err: unknown) => {
        if (!cancelled) report(err, onUnauthorized, setError, 'Could not load discount bundles.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [onUnauthorized]);

  const items = useMemo(() => categories.flatMap((category) => category.items), [categories]);

  const createNew = useCallback(
    (form: BundleFormState) => {
      setBusyKey('new');
      setNotice('');
      return createBundle({
        name: form.name.trim(),
        price_cents: toCents(form.price),
        active: form.active,
        groups: toInput(form.groups),
      })
        .then((created) => {
          setBundles((current) => [...current, created]);
          setError('');
          setNotice(created.name + ' created.');
          return created;
        })
        .catch((err: unknown) => {
          report(err, onUnauthorized, setError, 'Could not create the bundle.');
          return null;
        })
        .finally(() => setBusyKey(null));
    },
    [onUnauthorized]
  );

  const saveExisting = useCallback(
    (bundleId: number, form: BundleFormState) => {
      setBusyKey(String(bundleId));
      setNotice('');
      return updateBundle(bundleId, {
        name: form.name.trim(),
        price_cents: toCents(form.price),
        active: form.active,
        groups: toInput(form.groups),
      })
        .then((updated) => {
          setBundles((current) => current.map((bundle) => (bundle.id === bundleId ? updated : bundle)));
          setError('');
          setNotice(updated.name + ' saved.');
          return updated;
        })
        .catch((err: unknown) => {
          report(err, onUnauthorized, setError, 'Could not save the bundle.');
          return null;
        })
        .finally(() => setBusyKey(null));
    },
    [onUnauthorized]
  );

  const remove = useCallback(
    (bundleId: number) => {
      setBusyKey(String(bundleId));
      setNotice('');
      return deleteBundle(bundleId)
        .then(() => {
          setBundles((current) => current.filter((bundle) => bundle.id !== bundleId));
          setError('');
          setNotice('Bundle deleted.');
          return true;
        })
        .catch((err: unknown) => {
          report(err, onUnauthorized, setError, 'Could not delete the bundle.');
          return false;
        })
        .finally(() => setBusyKey(null));
    },
    [onUnauthorized]
  );

  const dismissError = useCallback(() => setError(''), []);

  return {
    categories,
    tags,
    bundles,
    items,
    loading,
    error,
    notice,
    busyKey,
    createNew,
    saveExisting,
    remove,
    dismissError,
  };
}
